import React, { useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";

const toName = (value) => (value ? String(value) : "-");
const capitalizeWord = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const formatPatientName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";

  if (raw.includes(",")) {
    const [lastName, firstName] = raw.split(",").map((part) => part.trim());
    return [firstName, lastName]
      .filter(Boolean)
      .map((part) => part.split(/\s+/).map(capitalizeWord).join(" "))
      .join(" ");
  }

  return raw
    .split(/\s+/)
    .map(capitalizeWord)
    .join(" ");
};

const getFirstLastName = (value) => {
  const formatted = formatPatientName(value);
  if (!formatted || formatted === "-") return { firstName: "", lastName: "" };
  const parts = formatted.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.length > 1 ? parts.slice(1).join(" ") : ""
  };
};
const toCodes = (value) =>
  String(value || "")
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);

const getProfileImageSrc = (profileImage) => {
  if (!profileImage) return "";
  if (String(profileImage).startsWith("http")) return profileImage;
  return `${process.env.REACT_APP_VIDEO_CALL_REQUEST}/${profileImage}`;
};

const CPT_CODE_COLORS = {
  99439: "#8b5cf6",
  99445: "#10b981",
  99453: "#f97316",
  99454: "#ef4444",
  99457: "#7c3aed",
  99458: "#ec4899",
  99470: "#14b8a6",
  99490: "#84cc17"
};

const getCptCodePillStyle = (code) => ({
  background: CPT_CODE_COLORS[code] || "#64748b",
  color: "#ffffff"
});

const getOrganizationIcon = (organization) => {
  if (!organization) return <i className="mdi mdi-hospital-building"></i>;
  const orgLower = String(organization).toLowerCase();
  if (orgLower.includes("hospital") || orgLower.includes("medical")) {
    return <i className="mdi mdi-hospital-building"></i>;
  }
  if (orgLower.includes("clinic")) {
    return <i className="mdi mdi-medical-clinic"></i>;
  }
  if (orgLower.includes("health") || orgLower.includes("wellness")) {
    return <i className="mdi mdi-heart-pulse"></i>;
  }
  if (orgLower.includes("care")) {
    return <i className="mdi mdi-hand-heart"></i>;
  }
  if (orgLower.includes("senior") || orgLower.includes("elder")) {
    return <i className="mdi mdi-account-heart"></i>;
  }
  return <i className="mdi mdi-hospital-building"></i>;
};

const { ExportCSVButton } = CSVExport;

const PatientProfileCell = ({ patientName, profileImage, email, phone, isActive }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = getProfileImageSrc(profileImage);
  const showImage = Boolean(imageSrc) && !imageError;

  return (
    <div className="mpr-patient-profile-cell">
      <div className="mpr-patient-profile-avatar-wrap">
        {showImage ? (
          <img
            src={imageSrc}
            alt={patientName}
            className="mpr-patient-profile-avatar"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className="mpr-patient-profile-fallback-icon">
            <i className="mdi mdi-account"></i>
          </span>
        )}
      </div>
      <div className="mpr-patient-profile-content">
        <div className="mpr-patient-profile-name-row">
          <div className="mpr-patient-profile-name">{patientName}</div>
          <span className={`mpr-patient-active-tag ${isActive ? "active" : "inactive"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
        <div className="mpr-patient-profile-meta">
          <i className="mdi mdi-email-outline"></i>
          <span>{toName(email)}</span>
        </div>
        <div className="mpr-patient-profile-meta">
          <i className="mdi mdi-phone-outline"></i>
          <span>{toName(phone)}</span>
        </div>
      </div>
    </div>
  );
};

const normalizePatients = (patients = []) =>
  patients.map((patient) => {
    const technicalMet = Boolean(patient?.technicalMet);
    const professionalMet = Boolean(patient?.professionalMet);
    const statusLabel = technicalMet && professionalMet ? "Met Both" : technicalMet || professionalMet ? "Met Only One" : "Met None";

    return {
      ...patient,
      _id: patient?._id || `${patient?.email || ""}-${patient?.phone || ""}`,
      patientName: formatPatientName(patient?.patientName),
      providerName: toName(patient?.mainCareProvider?.name),
      organization: toName(patient?.client?.organization),
      statusLabel,
      metCodesText: Array.isArray(patient?.metCodes) ? patient.metCodes.join(", ") : "-",
      baseTechnicalMet: Boolean(patient?.monthComparison?.baseMonth?.technicalMet),
      baseProfessionalMet: Boolean(patient?.monthComparison?.baseMonth?.professionalMet),
      comparedTechnicalMet: Boolean(patient?.monthComparison?.comparedMonth?.technicalMet),
      comparedProfessionalMet: Boolean(patient?.monthComparison?.comparedMonth?.professionalMet),
      baseCodesText: Array.isArray(patient?.monthComparison?.baseMonth?.metCptCodes)
        ? patient.monthComparison.baseMonth.metCptCodes.join(", ")
        : "-",
      comparedCodesText: Array.isArray(patient?.monthComparison?.comparedMonth?.metCptCodes)
        ? patient.monthComparison.comparedMonth.metCptCodes.join(", ")
        : "-",
      isActive: Boolean(patient?.isActive)
    };
  });

const MonthlyPerformancePatientTable = ({
  patients,
  searchText,
  onSearchTextChange,
  fileSuffix = "monthly-performance",
  baseMonthLabel = "Base Month",
  comparedMonthLabel = "Compared Month"
}) => {
  const DEFAULT_PAGE_SIZE = 10;
  const rows = useMemo(() => normalizePatients(patients), [patients]);

  const filteredRows = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    if (!query) return rows;

    return rows.filter((row) =>
      [row.patientName, row.email, row.phone, row.providerName, row.organization, row.statusLabel, row.metCodesText]
        .map((item) => String(item || "").toLowerCase())
        .some((item) => item.includes(query))
    );
  }, [rows, searchText]);

  const getStatusClass = (statusLabel) => {
    if (statusLabel === "Met Both") return "yes";
    if (statusLabel === "Met None") return "no";
    return "partial";
  };

  const columns = useMemo(
    () => [
      {
        dataField: "_id",
        text: "ID",
        hidden: true
      },
      {
        dataField: "patientName",
        text: "Patient",
        sort: true,
        sortFunc: (a, b, order) => {
          const aName = getFirstLastName(a);
          const bName = getFirstLastName(b);
          const directionMultiplier = order === "asc" ? 1 : -1;
          const firstNameCompare = aName.firstName.localeCompare(bName.firstName, undefined, { sensitivity: "base" });
          if (firstNameCompare !== 0) return firstNameCompare * directionMultiplier;
          return aName.lastName.localeCompare(bName.lastName, undefined, { sensitivity: "base" }) * directionMultiplier;
        },
        formatter: (cell, row) => (
          <PatientProfileCell
            patientName={row.patientName}
            profileImage={row.profileImage}
            email={row.email}
            phone={row.phone}
            isActive={row.isActive}
          />
        ),
        headerStyle: () => ({ minWidth: "280px" })
      },
      {
        dataField: "providerName",
        text: "Provider",
        sort: true
      },
      {
        dataField: "organization",
        text: "Organization",
        sort: true,
        formatter: (cell, row) => (
          <div className="mpr-org-cell-wrapper">
            <span className="mpr-org-icon">{getOrganizationIcon(row.organization)}</span>
            <span className="mpr-org-text">{row.organization}</span>
          </div>
        )
      },
      {
        dataField: "technicalMet",
        text: "Tech",
        sort: true,
        sortValue: (cell) => (cell ? 1 : 0),
        formatter: (cell) => <span className={`mpr-badge ${cell ? "yes" : "no"}`}>{cell ? "Met" : "Not Met"}</span>
      },
      {
        dataField: "professionalMet",
        text: "Prof",
        sort: true,
        sortValue: (cell) => (cell ? 1 : 0),
        formatter: (cell) => <span className={`mpr-badge ${cell ? "yes" : "no"}`}>{cell ? "Met" : "Not Met"}</span>
      },
      {
        dataField: "statusLabel",
        text: "Status",
        sort: true,
        formatter: (cell) => <span className={`mpr-badge mpr-status-badge ${getStatusClass(cell)}`}>{cell}</span>
      },
      {
        dataField: "metCodesText",
        text: "Met CPT Codes",
        sort: true,
        formatter: (cell, row) => (
          <div className="mpr-code-pill-wrap">
            {toCodes(cell).length > 0 ? (
              toCodes(cell).map((code) => (
                <span className="mpr-cpt-code-pill" style={getCptCodePillStyle(code)} key={`${row._id}-${code}`}>
                  {code}
                </span>
              ))
            ) : (
              <span>-</span>
            )}
          </div>
        ),
        headerStyle: () => ({ minWidth: "240px" })
      },
      {
        dataField: "baseCodesText",
        text: baseMonthLabel,
        sort: true,
        sortValue: (cell, row) => (row.baseTechnicalMet ? 1 : 0) + (row.baseProfessionalMet ? 1 : 0),
        formatter: (cell, row) => (
          <div className="mpr-month-compare">
            <span className={`mpr-badge mpr-month-pill ${row.baseTechnicalMet ? "yes" : "no"}`}>Tech {row.baseTechnicalMet ? "Met" : "Not"}</span>
            <span className={`mpr-badge mpr-month-pill ${row.baseProfessionalMet ? "yes" : "no"}`}>Prof {row.baseProfessionalMet ? "Met" : "Not"}</span>
            <div className="mpr-codes-text mpr-month-codes-wrap">
              {toCodes(cell).length > 0 ? (
                toCodes(cell).map((code) => (
                  <span className="mpr-month-code-pill" style={getCptCodePillStyle(code)} key={`${row._id}-base-${code}`}>
                    {code}
                  </span>
                ))
              ) : (
                <span className="mpr-month-code-empty">-</span>
              )}
            </div>
          </div>
        ),
        headerStyle: () => ({ minWidth: "230px" })
      },
      {
        dataField: "comparedCodesText",
        text: comparedMonthLabel,
        sort: true,
        sortValue: (cell, row) => (row.comparedTechnicalMet ? 1 : 0) + (row.comparedProfessionalMet ? 1 : 0),
        formatter: (cell, row) => (
          <div className="mpr-month-compare">
            <span className={`mpr-badge mpr-month-pill ${row.comparedTechnicalMet ? "yes" : "no"}`}>Tech {row.comparedTechnicalMet ? "Met" : "Not"}</span>
            <span className={`mpr-badge mpr-month-pill ${row.comparedProfessionalMet ? "yes" : "no"}`}>Prof {row.comparedProfessionalMet ? "Met" : "Not"}</span>
            <div className="mpr-codes-text mpr-month-codes-wrap">
              {toCodes(cell).length > 0 ? (
                toCodes(cell).map((code) => (
                  <span className="mpr-month-code-pill" style={getCptCodePillStyle(code)} key={`${row._id}-compared-${code}`}>
                    {code}
                  </span>
                ))
              ) : (
                <span className="mpr-month-code-empty">-</span>
              )}
            </div>
          </div>
        ),
        headerStyle: () => ({ minWidth: "230px" })
      }
    ],
    [baseMonthLabel, comparedMonthLabel]
  );

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    bootstrap4: true,
    sizePerPage: DEFAULT_PAGE_SIZE,
    totalSize: filteredRows.length,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: false,
    showTotal: filteredRows.length > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 }
    ]
  };

  return (
    <div className="mpr-patient-card">
      <ToolkitProvider
        keyField="_id"
        data={filteredRows}
        columns={columns}
        exportCSV={{ fileName: `${fileSuffix}.csv` }}
      >
        {(toolkitProps) => (
          <>
            <div className="mpr-patient-toolbar patient-list-toolbar">
              <div className="mpr-search-wrap patient-list-search-shell">
                <span className="patient-list-search-icon">
                  <i className="mdi mdi-magnify"></i>
                </span>
                <input
                  type="text"
                  className="patient-list-search-input"
                  placeholder="Search patient, email, provider, organization..."
                  value={searchText}
                  onChange={(event) => onSearchTextChange(event.target.value)}
                />
                {searchText && (
                  <button type="button" className="patient-list-search-clear" onClick={() => onSearchTextChange("")} aria-label="Clear search">
                    <i className="mdi mdi-close-circle"></i>
                  </button>
                )}
              </div>
              <ExportCSVButton {...toolkitProps.csvProps} className="mpr-export-btn patient-list-export-btn" disabled={filteredRows.length === 0}>
                <span className="patient-list-export-icon">
                  <i className="mdi mdi-file-export-outline"></i>
                </span>
                <span>Export CSV</span>
              </ExportCSVButton>
            </div>
            <BootstrapTable
              {...toolkitProps.baseProps}
              bootstrap4
              wrapperClasses="table-responsive"
              classes="mpr-patient-table"
              bordered={false}
              pagination={paginationFactory(paginationOptions)}
              noDataIndication={() => (
                <div className="mpr-empty-row">No patient records found for this filter.</div>
              )}
            />
          </>
        )}
      </ToolkitProvider>
    </div>
  );
};

export default MonthlyPerformancePatientTable;
